import { listIncidents } from '@/lib/api';
import type { Incident, IncidentSeverity, IncidentStatus, IncidentType } from '@/lib/types';
import { AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const INCIDENT_TYPES: IncidentType[] = [
  'bite',
  'escape',
  'injury',
  'illness',
  'behavioral',
  'property_damage',
  'lost_item',
  'other',
];

const INCIDENT_STATUSES: IncidentStatus[] = ['open', 'resolved', 'archived'];
const INCIDENT_SEVERITIES: IncidentSeverity[] = ['minor', 'moderate', 'severe', 'critical'];

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  minor: 'bg-green-100 text-green-800',
  moderate: 'bg-amber-100 text-amber-800',
  severe: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

function formatType(t: IncidentType): string {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function IncidentsPage() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | ''>('open');
  const [typeFilter, setTypeFilter] = useState<IncidentType | ''>('');
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | ''>('');
  const [followUpOnly, setFollowUpOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filtersToSend: Parameters<typeof listIncidents>[0] = {};
      if (statusFilter) filtersToSend.status = statusFilter;
      if (typeFilter) filtersToSend.type = typeFilter;
      if (severityFilter) filtersToSend.severity = severityFilter;
      if (followUpOnly) filtersToSend.followUpRequired = true;
      const res = await listIncidents(filtersToSend);
      setIncidents(res.incidents);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, severityFilter, followUpOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-[100dvh]">
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Incidents</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record and track safety and welfare incidents involving pets.
            </p>
          </div>
          <Link
            to="/incidents/new"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Report incident
          </Link>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as IncidentStatus | '')}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          >
            <option value="">All statuses</option>
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as IncidentType | '')}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          >
            <option value="">All types</option>
            {INCIDENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {formatType(t)}
              </option>
            ))}
          </select>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as IncidentSeverity | '')}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          >
            <option value="">All severities</option>
            {INCIDENT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={followUpOnly}
              onChange={(e) => setFollowUpOnly(e.target.checked)}
              className="rounded"
            />
            Follow-up due
          </label>
        </div>

        {error && (
          <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : incidents.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 opacity-40" />
            <p className="text-sm">No incidents found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Severity</th>
                  <th className="px-3 py-2">Pet</th>
                  <th className="px-3 py-2">Occurred</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Owner notified</th>
                  <th className="px-3 py-2">Follow-up</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {incidents.map((incident) => (
                  <tr key={incident.id} className="hover:bg-secondary/30 transition-colors">
                    <td className="px-3 py-3 font-medium capitalize">
                      {formatType(incident.incidentType)}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${SEVERITY_COLORS[incident.severity]}`}
                      >
                        {incident.severity}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {incident.petName ?? (
                        <span className="font-mono text-muted-foreground">
                          {incident.petId.slice(-8)}
                        </span>
                      )}
                      {incident.customerName && (
                        <p className="text-[10px] text-muted-foreground">{incident.customerName}</p>
                      )}
                    </td>
                    <td className="px-3 py-3">{formatDate(incident.occurredAt)}</td>
                    <td className="px-3 py-3 capitalize">{incident.status}</td>
                    <td className="px-3 py-3">
                      {incident.ownerNotifiedAt ? (
                        <span className="text-green-700 text-xs">
                          {formatDate(incident.ownerNotifiedAt)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">Not yet</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {incident.followUpRequired === 1 ? (
                        <span
                          className={`text-xs ${
                            incident.followUpDueOn &&
                            incident.followUpDueOn <= new Date().toISOString().slice(0, 10)
                              ? 'text-destructive font-medium'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {incident.followUpDueOn ?? 'Yes'}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Link
                        to={`/incidents/${incident.id}`}
                        className="text-xs text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
